import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { palette, fontFamily } from '../../styles/tokens';

type Status = 'loading' | 'confirmed' | 'already_confirmed' | 'error';

export function ConfirmBookingPage() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    if (!bookingId || !token) {
      setStatus('error');
      return;
    }

    (async () => {
      try {
        const { data, error } = await supabase.rpc('confirm_booking', {
          p_booking_id: bookingId,
          p_token: token,
        });
        if (error) { setStatus('error'); return; }
        if (data?.success && data?.already_confirmed) { setStatus('already_confirmed'); return; }
        if (data?.success) { setStatus('confirmed'); return; }
        setStatus('error');
      } catch {
        setStatus('error');
      }
    })();
  }, [bookingId, token]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#F5F5F5', fontFamily, padding: 24,
    }}>
      <div style={{
        textAlign: 'center', padding: 48, background: '#fff', borderRadius: 16,
        maxWidth: 420, width: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: 16, margin: '0 auto 20px',
          background: status === 'error'
            ? `linear-gradient(135deg, ${palette.red}, #C62828)`
            : `linear-gradient(135deg, ${palette.green}, #2E7D32)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {status === 'loading' && (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white" style={{ animation: 'spin 1s linear infinite' }}>
              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" fill="none" strokeDasharray="40 60" />
            </svg>
          )}
          {(status === 'confirmed' || status === 'already_confirmed') && (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
          {status === 'error' && (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          )}
        </div>

        {status === 'loading' && (
          <>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: palette.g800, margin: '0 0 8px' }}>Confirming...</h2>
            <p style={{ fontSize: 14, color: palette.g500, margin: 0 }}>Please wait while we confirm your appointment.</p>
          </>
        )}

        {status === 'confirmed' && (
          <>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: palette.g800, margin: '0 0 8px' }}>Appointment Confirmed</h2>
            <p style={{ fontSize: 14, color: palette.g500, margin: 0, lineHeight: 1.5 }}>
              Your appointment has been confirmed. You're all set. If you need to make changes, contact your service provider directly.
            </p>
          </>
        )}

        {status === 'already_confirmed' && (
          <>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: palette.g800, margin: '0 0 8px' }}>Already Confirmed</h2>
            <p style={{ fontSize: 14, color: palette.g500, margin: 0, lineHeight: 1.5 }}>
              This appointment was already confirmed. No action needed.
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: palette.g800, margin: '0 0 8px' }}>Link Expired</h2>
            <p style={{ fontSize: 14, color: palette.g500, margin: 0, lineHeight: 1.5 }}>
              This confirmation link is no longer valid. It may have already been used or the booking was updated.
            </p>
          </>
        )}

        <div style={{ marginTop: 24 }}>
          <a href="https://crewbit.ca" style={{
            display: 'inline-block', padding: '10px 24px', borderRadius: 8,
            background: palette.accent, color: '#fff', fontSize: 14, fontWeight: 600,
            textDecoration: 'none',
          }}>Visit CrewBit</a>
        </div>

        <p style={{ fontSize: 11, color: '#C7C7CC', marginTop: 24, marginBottom: 0 }}>
          Powered by CrewBit &middot; crewbit.ca
        </p>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
